import api from './client';

// 完整的用户资料类型（包含所有可能的字段）
export interface UserProfile {
  id: number | string;
  username: string;
  email: string;
  display_name?: string;
  full_name?: string;
  bio?: string;
  avatar?: string;
  account_level?: string;
  subscription_status?: string;
  quota_type?: string;
  monthly_video_quota?: number;
  yearly_quota?: number;
  permanent_quota?: number;
  permanent_quota_available?: number;  // 永久积分剩余可用量
  used_video_quota?: number;
  total_videos_generated?: number;
  quota_reset_date?: string;
  quota_reset_period?: number;
  registered_at?: string;
  last_login_at?: string;
  created_at?: string;
  updated_at?: string;
  subscription_start_date?: string;
  activation_code?: string;
  is_active: boolean;
  is_suspended?: boolean;
  is_email_verified?: boolean;
  email_notifications?: boolean;
  permissions?: any[];
  membership_level: string;
  quota_remaining?: number;
  total_quota_used?: number;
}

export interface UserQuota {
  remaining: number;
  total_used: number;
  membership_level: string;
}

export interface UserStats {
  total_videos: number;
  completed_videos: number;
  total_tasks: number;
  completed_tasks: number;
  success_rate: number;
  membership_level: string;
  quota_remaining: number;
}

// 统一的用户数据响应类型
export interface UserDataResponse {
  profile: UserProfile;
  stats: UserStats;
  membership: {
    account_level: string;
    subscription_status: string;
    quota_type: string;
    monthly_video_quota: number;
    yearly_quota: number;
    permanent_quota: number;
    permanent_quota_available?: number;  // 永久积分剩余可用量
    used_video_quota: number;
    quota_remaining: number;
    quota_reset_date?: string;
    quota_reset_period: number;
    subscription_start_date?: string;
    activation_code?: string;
    email: string;
    is_active: boolean;
    is_suspended: boolean;
    is_email_verified: boolean;
  };
  referral: {
    enabled: boolean;
    referral_code?: string;
    commission_balance: number;
    total_commission_earned: number;
    bound_referral_code?: string;
    bound_referrer_username?: string;
    level1: {
      count: number;
      total_commission: number;
      referrals: any[];
    };
    level2: {
      count: number;
      total_commission: number;
      referrals: any[];
    };
  };
}

export const usersAPI = {
  // 获取用户资料
  getProfile: async (): Promise<UserProfile> => {
    const response = await api.get('/api/users/profile');
    return response.data;
  },

  // 更新用户资料
  updateProfile: async (data: { 
    username?: string;
    display_name?: string;
    full_name?: string;
    email_notifications?: boolean;
    bio?: string;
  }): Promise<{ success: boolean; message?: string; data?: any }> => {
    const response = await api.put('/api/users/profile', data);
    return response.data;
  },

  // 获取用户积分信息
  getQuota: async (): Promise<UserQuota> => {
    const response = await api.get('/api/users/quota');
    return response.data;
  },

  // 充值积分
  rechargeQuota: async (amount: number): Promise<{ new_balance: number }> => {
    const response = await api.post('/api/users/quota/recharge', { amount });
    return response.data;
  },

  // 获取用户统计信息
  getStats: async (): Promise<UserStats> => {
    const response = await api.get('/api/users/stats');
    return response.data;
  },

  // 获取用户所有数据（统一接口）
  getUserData: async (): Promise<UserDataResponse> => {
    const response = await api.get('/api/users/data');
    return response.data;
  },

  // 扣除积分（用于视频复刻）
  deductPoints: async (amount: number): Promise<{ success: boolean; new_balance: number; error?: string }> => {
    const response = await api.post('/api/users/points/deduct', { amount });
    return response.data;
  },
};
